import type { ReactNode } from 'react';
import styles from './EmptyState.module.css';

/**
 * An empty report or card, drawn rather than left as a bare line (16 Oct):
 * a small picture of what would be here, in the theme's own colours, and the
 * sentence that says why there is nothing. The picture is decoration; the
 * sentence is what a screen reader reads.
 */
export type EmptyArt = 'bars' | 'clear' | 'pages' | 'clock' | 'agents';

export function EmptyState({ art, children, testId }: { art: EmptyArt; children: ReactNode; testId?: string }) {
  return (
    <div className={styles.empty} data-testid={testId ?? 'empty-state'} data-art={art}>
      <svg className={styles.art} viewBox="0 0 64 48" width={64} height={48} aria-hidden="true" focusable="false">{DRAWINGS[art]}</svg>
      <div className={styles.text}>{children}</div>
    </div>
  );
}

const DRAWINGS: Record<EmptyArt, ReactNode> = {
  // Bars waiting for numbers.
  bars: <>
    <line x1="8" y1="40" x2="56" y2="40" stroke="var(--chart-axis)" strokeOpacity="0.5" strokeWidth="1.5" strokeLinecap="round" />
    <rect x="12" y="24" width="8" height="16" rx="2" fill="var(--chart-grid)" />
    <rect x="24" y="14" width="8" height="26" rx="2" fill="var(--chart-blue)" fillOpacity="0.28" />
    <rect x="36" y="20" width="8" height="20" rx="2" fill="var(--chart-grid)" />
    <rect x="48" y="30" width="8" height="10" rx="2" fill="var(--chart-grid)" />
  </>,
  // Nothing wrong: a shield with a tick.
  clear: <>
    <path d="M32 6 L48 12 V24 C48 33 41 39 32 42 C23 39 16 33 16 24 V12 Z" fill="var(--chart-green)" fillOpacity="0.14" stroke="var(--chart-green)" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M25 24 L30 29 L39 19" fill="none" stroke="var(--chart-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </>,
  // A page with nothing on it yet.
  pages: <>
    <rect x="18" y="6" width="28" height="36" rx="3" fill="var(--surface)" stroke="var(--chart-axis)" strokeOpacity="0.6" strokeWidth="1.5" />
    <line x1="24" y1="15" x2="40" y2="15" stroke="var(--chart-grid)" strokeWidth="3" strokeLinecap="round" />
    <line x1="24" y1="22" x2="36" y2="22" stroke="var(--chart-grid)" strokeWidth="3" strokeLinecap="round" />
    <line x1="24" y1="29" x2="38" y2="29" stroke="var(--chart-grid)" strokeWidth="3" strokeLinecap="round" />
    <circle cx="44" cy="36" r="7" fill="var(--chart-blue)" fillOpacity="0.2" stroke="var(--chart-blue)" strokeWidth="1.5" />
  </>,
  // Time not yet measured.
  clock: <>
    <circle cx="32" cy="24" r="16" fill="var(--chart-violet)" fillOpacity="0.1" stroke="var(--chart-violet)" strokeWidth="1.5" />
    <path d="M32 14 V24 L39 28" fill="none" stroke="var(--chart-violet)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </>,
  // An agent with nothing to report.
  agents: <>
    <line x1="32" y1="6" x2="32" y2="12" stroke="var(--chart-magenta)" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="32" cy="5" r="2" fill="var(--chart-magenta)" />
    <rect x="16" y="12" width="32" height="24" rx="7" fill="var(--chart-magenta)" fillOpacity="0.12" stroke="var(--chart-magenta)" strokeWidth="1.5" />
    <circle cx="26" cy="24" r="2.5" fill="var(--chart-magenta)" />
    <circle cx="38" cy="24" r="2.5" fill="var(--chart-magenta)" />
    <line x1="22" y1="42" x2="42" y2="42" stroke="var(--chart-grid)" strokeWidth="3" strokeLinecap="round" />
  </>,
};
